/**
 * ArticleByline (G2, D9/D10, P1/P2): avatar (photo, else the initial) · "Name · jobTitle, org" ·
 * Published <time> [· Updated <time>] · N min read. Data: articleRuntime.byline (written at publish).
 * Dates use the document's defaultLanguage in UTC so the client re-render never changes them (PR12).
 */
import React, { memo } from 'react';
import type { ArticleBlockProps } from '@modules/screen-view-v2/components/screens/FlexibleLayout/article/types';
import { formatArticleDate } from '@modules/screen-view-v2/utils/articleDate';

const WORDS = { published: 'Published', updated: 'Updated', minRead: 'min read' } as const;

export const ArticleByline = memo(function ArticleByline({ article }: ArticleBlockProps): React.ReactElement | null {
  const byline = article.runtime?.byline;
  if (!byline) return null;
  const published = formatArticleDate(byline.datePublished, article.defaultLanguage);
  const updated = formatArticleDate(byline.dateModified, article.defaultLanguage);
  const role = [byline.jobTitle, byline.org].filter(Boolean).join(', ');
  const initial = byline.name.trim().charAt(0).toUpperCase();
  return (
    <div className="ap-block ap-byline">
      {byline.avatarUrl
        ? <img className="ap-avatar" src={byline.avatarUrl} alt="" />
        : <span className="ap-avatar" aria-hidden="true">{initial}</span>}
      <div className="ap-byline-text">
        <p className="ap-byline-name"><strong>{byline.name}</strong>{role ? ` · ${role}` : ''}</p>
        <p className="ap-byline-meta">
          {published ? `${WORDS.published} ` : null}
          {published ? <time dateTime={byline.datePublished}>{published}</time> : null}
          {updated && updated !== published ? ` · ${WORDS.updated} ` : null}
          {updated && updated !== published ? <time dateTime={byline.dateModified}>{updated}</time> : null}
          {`${published ? ' · ' : ''}${byline.readingMinutes} ${WORDS.minRead}`}
        </p>
      </div>
    </div>
  );
});

ArticleByline.displayName = 'ArticleByline';
